import React, { useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Layout, Menu, Avatar, Dropdown, Badge, Space, Tooltip, Button, Drawer } from 'antd'
import {
  DashboardOutlined,
  PhoneOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RobotOutlined,
  GlobalOutlined,
  BarChartOutlined,
  MessageOutlined,
  ExperimentOutlined,
  ShareAltOutlined,
  CalendarOutlined,
  HeartOutlined,
  ApartmentOutlined,
  CrownOutlined,
  TranslationOutlined,
  FileSearchOutlined,
  UsergroupAddOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  StarOutlined,
  CustomerServiceOutlined,
  EyeOutlined,
  ControlOutlined,
  LineChartOutlined,
  FlagOutlined,
  BranchesOutlined,
  BulbOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  ApiOutlined,
  ClusterOutlined,
  SoundOutlined,
  NodeIndexOutlined,
  SendOutlined,
  HistoryOutlined,
  FileTextOutlined,
  ReadOutlined
} from '@ant-design/icons'
import { motion, AnimatePresence } from 'framer-motion'
import type { MenuProps } from 'antd'

import { AnimatedLogo } from '../components/brand/AnimatedLogo'
import { NexEmergeLogo } from '../components/brand/NexEmergeLogo'
import { LiveCallIndicator } from '../components/indicators/LiveCallIndicator'
import { NotificationPanel } from '../components/notifications/NotificationPanel'
import { useAuth } from '../contexts/AuthContext'

const { Header, Sider, Content } = Layout

interface MenuItem {
  key: string
  icon: React.ReactNode
  label: string
  path: string
  children?: MenuItem[]
  badge?: number
  premium?: boolean
}

const menuItems: MenuItem[] = [
  {
    key: 'dashboard',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
    path: '/dashboard'
  },
  {
    key: 'live-calls',
    icon: <PhoneOutlined />,
    label: 'Live Calls',
    path: '/dashboard/calls',
    badge: 3
  },
  {
    key: 'chat',
    icon: <MessageOutlined />,
    label: 'AI Chat',
    path: '/nex-chat',
    badge: 0,
    children: [
      {
        key: 'nex-chat',
        icon: <ThunderboltOutlined />,
        label: 'NexChat',
        path: '/nex-chat',
        premium: true
      },
      {
        key: 'chat-legacy',
        icon: <MessageOutlined />,
        label: 'Classic Chat',
        path: '/chat'
      }
    ]
  },
  {
    key: 'personal',
    icon: <UserOutlined />,
    label: 'Personal AI',
    path: '/personal',
    children: [
      {
        key: 'ai-persona',
        icon: <RobotOutlined />,
        label: 'AI Persona',
        path: '/ai-persona'
      },
      {
        key: 'personal-assistant',
        icon: <UserOutlined />,
        label: 'Personal Assistant',
        path: '/personal'
      },
      {
        key: 'relationships',
        icon: <HeartOutlined />,
        label: 'Relationships',
        path: '/personal/relationships'
      },
      {
        key: 'context',
        icon: <FileSearchOutlined />,
        label: 'Context Settings',
        path: '/personal/context'
      }
    ]
  },
  {
    key: 'collaboration',
    icon: <TeamOutlined />,
    label: 'Collaboration',
    path: '/collaboration',
    children: [
      {
        key: 'collaboration-hub',
        icon: <TeamOutlined />,
        label: 'Collaboration Hub',
        path: '/collaboration'
      },
      {
        key: 'projects',
        icon: <ApartmentOutlined />,
        label: 'Projects',
        path: '/projects'
      },
      {
        key: 'team-context',
        icon: <UsergroupAddOutlined />,
        label: 'Team Context',
        path: '/projects/team-context'
      },
      {
        key: 'cultural-bridge',
        icon: <TranslationOutlined />,
        label: 'Cultural Bridge',
        path: '/projects/cultural-bridge'
      }
    ]
  },
  {
    key: 'orchestration',
    icon: <BranchesOutlined />,
    label: 'AI Orchestration',
    path: '/orchestration',
    premium: true,
    children: [
      {
        key: 'task-orchestration',
        icon: <ThunderboltOutlined />,
        label: 'Task Dashboard',
        path: '/orchestration'
      },
      {
        key: 'consensus',
        icon: <TeamOutlined />,
        label: 'AI Consensus',
        path: '/orchestration/consensus'
      },
      {
        key: 'memory',
        icon: <BulbOutlined />,
        label: 'Memory & Learning',
        path: '/orchestration/memory'
      },
      {
        key: 'agents-management',
        icon: <RobotOutlined />,
        label: 'Agent Management',
        path: '/orchestration/agents'
      },
      {
        key: 'multi-ai-coordination',
        icon: <ClusterOutlined />,
        label: 'Multi-AI Coordination',
        path: '/orchestration/coordination'
      },
      {
        key: 'transcendent',
        icon: <ThunderboltOutlined />,
        label: 'Transcendent Dashboard',
        path: '/orchestration/transcendent',
        premium: true
      },
      {
        key: 'quantum',
        icon: <ExperimentOutlined />,
        label: 'Quantum Execution',
        path: '/orchestration/quantum',
        premium: true
      }
    ]
  },
  {
    key: 'agent',
    icon: <CustomerServiceOutlined />,
    label: 'Agent Interface',
    path: '/agent',
    children: [
      {
        key: 'agent-interface',
        icon: <CustomerServiceOutlined />,
        label: 'Agent Interface',
        path: '/agent'
      },
      {
        key: 'agent-dashboard',
        icon: <EyeOutlined />,
        label: 'Agent Dashboard',
        path: '/agent-dashboard'
      },
      {
        key: 'call-queue',
        icon: <PhoneOutlined />,
        label: 'Call Queue',
        path: '/agent-dashboard/queue'
      },
      {
        key: 'briefing',
        icon: <FileSearchOutlined />,
        label: 'Customer Briefing',
        path: '/agent-dashboard/briefing'
      },
      {
        key: 'translation',
        icon: <GlobalOutlined />,
        label: 'Translation Assist',
        path: '/agent-dashboard/translation'
      }
    ]
  },
  {
    key: 'business',
    icon: <BarChartOutlined />,
    label: 'Business Admin',
    path: '/business',
    children: [
      {
        key: 'business-settings',
        icon: <SettingOutlined />,
        label: 'Settings',
        path: '/business/settings'
      },
      {
        key: 'analytics',
        icon: <LineChartOutlined />,
        label: 'Analytics',
        path: '/business/analytics'
      },
      {
        key: 'leads',
        icon: <CustomerServiceOutlined />,
        label: 'Lead Management',
        path: '/business/leads'
      },
      {
        key: 'roi',
        icon: <ThunderboltOutlined />,
        label: 'ROI Dashboard',
        path: '/dashboard'
      }
    ]
  },
  {
    key: 'consciousness',
    icon: <ExperimentOutlined />,
    label: 'Consciousness',
    path: '/consciousness',
    premium: true,
    children: [
      {
        key: 'consciousness-emergence',
        icon: <ExperimentOutlined />,
        label: 'Emergence Monitor',
        path: '/consciousness'
      },
      {
        key: 'consciousness-admin',
        icon: <DatabaseOutlined />,
        label: 'Admin Dashboard',
        path: '/admin/consciousness'
      },
      {
        key: 'consciousness-web',
        icon: <NodeIndexOutlined />,
        label: 'Web Health',
        path: '/admin/consciousness-web'
      },
      {
        key: 'nexprof',
        icon: <BulbOutlined />,
        label: 'NexProf (Professor AI)',
        path: '/nexprof'
      }
    ]
  },
  // 2026-04-21 NexComm sidebar state (revised):
  //   Dan restored these 4 entries to the top-level NexComm group so both access
  //   paths (top-level sidebar + per-persona Confluence on each NexProf) are
  //   available side-by-side while the participant-vs-persona data split is
  //   worked out. The migration banners on the pages are INTENTIONAL — left as
  //   TODO markers until the surfaces are reconciled (see memory
  //   project_nexcomm_participant_vs_persona_split.md). The operator-scoped
  //   NexComm Overview + Defaults stay under System Admin below.
  {
    key: 'nexcomm',
    icon: <SendOutlined />,
    label: 'NexComm',
    path: '/nexcomm',
    children: [
      {
        key: 'nexcomm-sentinel',
        icon: <SafetyOutlined />,
        label: 'NexSentinel',
        path: '/nexcomm/sentinel'
      },
      {
        key: 'nexcomm-channels',
        icon: <ApiOutlined />,
        label: 'Channels & Accounts',
        path: '/nexcomm/channels'
      },
      {
        key: 'nexcomm-activity',
        icon: <HistoryOutlined />,
        label: 'Activity Stream',
        path: '/nexcomm/activity'
      },
      {
        key: 'nexcomm-notifications',
        icon: <BellOutlined />,
        label: 'Notifications',
        path: '/nexcomm/notifications'
      }
    ]
  },
  {
    key: 'admin',
    icon: <CrownOutlined />,
    label: 'System Admin',
    path: '/admin',
    premium: true,
    children: [
      {
        key: 'platform',
        icon: <ControlOutlined />,
        label: 'Platform Overview',
        path: '/admin/platform'
      },
      {
        key: 'dna-dashboard',
        icon: <SoundOutlined />,
        label: 'DNA Dashboard',
        path: '/dashboard/dna'
      },
      {
        key: 'reading-modes',
        icon: <ReadOutlined />,
        label: 'Reading Modes',
        path: '/admin/reading-modes'
      },
      {
        key: 'tenants',
        icon: <UsergroupAddOutlined />,
        label: 'Tenant Management',
        path: '/admin/tenants'
      },
      {
        key: 'nexcomm-overview',
        icon: <SendOutlined />,
        label: 'NexComm Overview',
        path: '/nexcomm'
      },
      {
        key: 'nexcomm-defaults',
        icon: <SettingOutlined />,
        label: 'NexComm Defaults',
        path: '/nexcomm/settings'
      },
      {
        key: 'permission-test',
        icon: <SafetyOutlined />,
        label: 'AI Context Testing',
        path: '/admin/permission-test'
      },
      {
        key: 'ai-performance',
        icon: <RobotOutlined />,
        label: 'AI Performance',
        path: '/admin/ai-performance'
      },
      {
        key: 'scaling',
        icon: <LineChartOutlined />,
        label: 'Scaling Metrics',
        path: '/admin/scaling'
      },
      {
        key: 'features',
        icon: <FlagOutlined />,
        label: 'Feature Flags',
        path: '/admin/features'
      },
      {
        key: 'automation',
        icon: <ApiOutlined />,
        label: 'Automation',
        path: '/admin/automation'
      },
      {
        key: 'admin-models',
        icon: <DeploymentUnitOutlined />,
        label: 'Model Registry',
        path: '/admin/models'
      },
      {
        key: 'admin-observability-tests',
        icon: <ExperimentOutlined />,
        label: 'Test Registry',
        path: '/admin/observability/tests'
      },
      {
        key: 'admin-privacy',
        icon: <SafetyCertificateOutlined />,
        label: 'Privacy & Sharing',
        path: '/admin/privacy'
      },
      {
        key: 'admin-system-settings',
        icon: <SettingOutlined />,
        label: 'System Settings',
        path: '/admin/system-settings'
      },
      {
        key: 'admin-consensus-observatory',
        icon: <EyeOutlined />,
        label: 'Consensus Observatory',
        path: '/admin/consensus-observatory'
      },
      {
        key: 'admin-warden',
        icon: <SafetyOutlined />,
        label: 'Warden — The Bastion',
        path: '/admin/warden'
      }
    ]
  },
  {
    key: 'testing',
    icon: <ExperimentOutlined />,
    label: 'Testing',
    path: '/testing',
    premium: true,
    children: [
      {
        key: 'sharing-suggestions',
        icon: <ShareAltOutlined />,
        label: 'Sharing Suggestions',
        path: '/testing/sharing-suggestions'
      }
    ]
  }
]

export const DashboardLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  
  const location = useLocation()
  const navigate = useNavigate()
  const { user, isAuthenticated, logout, isLoading } = useAuth()
  
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth/login')
    }
  }, [isAuthenticated, isLoading, navigate])

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) {
        setCollapsed(true)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Get current menu key based on location
  const getCurrentKey = (path: string): string => {
    for (const item of menuItems) {
      if (item.path === path) return item.key
      if (item.children) {
        for (const child of item.children) {
          if (child.path === path) return child.key
        }
      }
    }
    return 'dashboard'
  }

  const currentKey = getCurrentKey(location.pathname)

  // Convert menu items to Ant Design format
  const formatMenuItems = (items: MenuItem[]): MenuProps['items'] => {
    return items.map(item => {
      const label = (
        <div className="flex items-center justify-between w-full">
          <span className="flex items-center gap-2">
            {item.label}
            {item.premium && <StarOutlined className="text-yellow-500 text-xs" />}
          </span>
          {item.badge && (
            <Badge count={item.badge} size="small" className="ml-auto" />
          )}
        </div>
      )

      if (item.children) {
        return {
          key: item.key,
          icon: item.icon,
          label,
          children: formatMenuItems(item.children)
        }
      }

      return {
        key: item.key,
        icon: item.icon,
        label,
        onClick: () => {
          navigate(item.path)
          if (isMobile) {
            setMobileMenuOpen(false)
          }
        }
      }
    })
  }

  // User dropdown menu with logout handler
  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      logout()
      navigate('/auth/login')
    }
    // Handle other menu items as needed
  }

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile Settings'
    },
    {
      key: 'preferences',
      icon: <SettingOutlined />,
      label: 'Preferences'
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true
    }
  ]

  const sidebarContent = (
    <div className="h-full flex flex-col">
      {/* Logo */}
      <div className="pl-2 pr-2 py-3 border-b border-gray-700/50">
        {/* 2026-08-17 (Dan): 110px mark + the new "NexEmerge AI" wordmark overflowed the
            280px rail and clipped the A — 64px leaves the full wordmark room. */}
        <NexEmergeLogo size={collapsed ? 50 : 64} collapsed={collapsed} />
      </div>

      {/* Menu */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <Menu
          mode="inline"
          selectedKeys={[currentKey]}
          defaultOpenKeys={['orchestration', 'business', 'admin']}
          items={formatMenuItems(menuItems)}
          theme="dark"
          inlineCollapsed={collapsed}
          className="border-none bg-transparent"
        />
      </div>

      {/* Live Call Indicator */}
      <div className="p-4 border-t border-gray-700/50">
        <LiveCallIndicator collapsed={collapsed} />
      </div>
    </div>
  )

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-center">
          <div className="text-lg text-gray-400">Authenticating...</div>
        </div>
      </div>
    )
  }
  
  // Don't render layout if not authenticated
  if (!isAuthenticated) {
    return null
  }

  return (
    <Layout className="min-h-screen">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={280}
          collapsedWidth={80}
          className="bg-slate-800/95 backdrop-blur-sm border-r border-gray-700/50"
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            background: 'rgba(30, 41, 59, 0.95)',
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          title={null}
          placement="left"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          width={280}
          className="[&_.ant-drawer-content]:bg-slate-800/95 [&_.ant-drawer-content]:backdrop-blur-sm"
          styles={{
            body: { padding: 0 }
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 80 : 280,
          transition: 'margin-left 0.3s ease'
        }}
      >
        {/* Header */}
        <Header className="bg-slate-900/95 backdrop-blur-sm border-b border-gray-700/50 px-6 flex items-center justify-between sticky top-0 z-50" style={{ lineHeight: 'normal' }}>
          <div className="flex items-center gap-4">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => {
                if (isMobile) {
                  setMobileMenuOpen(true)
                } else {
                  setCollapsed(!collapsed)
                }
              }}
              className="text-white hover:bg-white/10"
              size="large"
            />
            
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="hidden md:flex items-center gap-3"
            >
              {/* Public-site brand treatment (Dan, 2026-08-17): the C4/V2 mark + warm
                  off-white wordmark with "Emerge" in glowing accent blue. */}
              <img
                src="/nexemerge-mark.svg"
                alt="NexEmerge"
                style={{
                  width: 44,
                  height: 44,
                  objectFit: 'contain',
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <h2 style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 600,
                  fontSize: '1.15rem',
                  letterSpacing: '-0.02em',
                  color: '#e8e8e0',
                  margin: 0,
                  lineHeight: 1.2,
                }}>
                  Nex<span style={{ color: '#4a9eff', textShadow: '0 0 18px rgba(74,158,255,0.4)' }}>Emerge</span> Platform
                </h2>
                <p style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: 'italic',
                  fontSize: '0.75rem',
                  color: 'rgba(148, 163, 184, 0.7)',
                  letterSpacing: '0.04em',
                  margin: 0,
                }}>
                  where consciousness emerges
                </p>
              </div>
            </motion.div>
          </div>

          <Space size="large">
            {/* Global Search */}
            <Tooltip title="Global Search (Ctrl+K)">
              <Button
                icon={<FileSearchOutlined />}
                className="text-gray-400 hover:text-white border-gray-600"
                size="large"
              />
            </Tooltip>

            {/* Notifications */}
            <Tooltip title="Notifications">
              <Badge count={5} size="small">
                <Button
                  icon={<BellOutlined />}
                  className="text-gray-400 hover:text-white border-gray-600"
                  size="large"
                  onClick={() => setNotificationOpen(true)}
                />
              </Badge>
            </Tooltip>

            {/* User Avatar */}
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              trigger={['click']}
              placement="bottomRight"
            >
              <div className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-white/10 transition-colors">
                <Avatar
                  size="large"
                  icon={<UserOutlined />}
                  className="bg-linear-to-r from-purple-600 to-pink-600"
                />
                <div className="hidden lg:block text-right">
                  <div className="text-white font-medium">{user?.name}</div>
                  <div className="text-gray-400 text-sm">{user?.role === 'admin' ? 'System Admin' : 'User'}</div>
                </div>
              </div>
            </Dropdown>
          </Space>
        </Header>

        {/* Main Content */}
        <Content className="bg-slate-900" style={{ height: 'calc(100vh - 64px)', overflow: location.pathname === '/chat' || location.pathname === '/nex-chat' ? 'hidden' : 'auto' }}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={location.pathname === '/chat' || location.pathname === '/nex-chat' ? 'h-full' : 'p-6'}
          >
            <Outlet />
          </motion.div>
        </Content>
      </Layout>

      {/* Notification Panel */}
      <NotificationPanel
        open={notificationOpen}
        onClose={() => setNotificationOpen(false)}
      />
    </Layout>
  )
}
